<script>
import dayjs from 'dayjs';
import {
  prefetchDataMixin,
  pageTitleMixin,
} from '@ubic/vue-mixins';
import { permissions } from '@/constants';
import ErrorAccessDenied from '@/components/ErrorAccessDenied';
import IconFileDownload from '@/components/icons/FileDownload';
import IconPlus from '@/components/icons/Plus';
import LogsList from './components/LogsList';
import LogsFilter from './components/LogsFilter';
import service from './service';

const FILTER_DELAY = 1000;

export default {
  pageTitle: 'Лог действий',
  async prefetchData () {
    if (!this.hasReadPermission) {
      return;
    }

    this.isFetching = true;

    this.filter = { ...this.filter, ...this.$route.query };

    [this.workerOptions, this.projectOptions] = await Promise.all([
      this.hasWorkerReadPermission
        ? service.workerOptions()
        : [],
      this.hasProjectReadPermission
        ? service.projectOptions()
        : [],
      this.fetchLogs(),
    ]);

    this.isFetching = false;
  },
  mixins: [
    prefetchDataMixin,
    pageTitleMixin,
  ],
  data () {
    return {
      isFetching: false,
      logs: [],
      workerOptions: [],
      projectOptions: [],
      filter: {
        worker: '',
        project: '',
        minDate: dayjs().startOf('day').subtract(7, 'day').format('YYYY-MM-DD'),
        maxDate: dayjs().startOf('day').format('YYYY-MM-DD'),
      },
      filterTimeout: null,
    };
  },
  computed: {
    hasReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.LOG_READ);
    },
    hasWorkerReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.WORKER_READ);
    },
    hasProjectReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.PROJECT_READ);
    },
    hasTaskReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.LOG_READ_TASK);
    },
    hasWritePermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.LOG_WRITE);
    },
    dateIsWeekend () {
      return this.$store.getters['weekends/dateIsWeekend'];
    },
  },
  methods: {
    async fetchLogs () {
      this.logs = Object.freeze(await service.getList(this.filter));
    },
    updateQueryString () {
      this.$router.replace({
        path: this.$route.path,
        query: { ...this.filter },
      });
    },
    handleFilterChange (value) {
      this.filter = value;

      this.clearFilterTimeout();

      this.isFetching = true;
      this.updateQueryString();

      setTimeout(async () => {
        await this.fetchLogs();
        this.isFetching = false;
      }, FILTER_DELAY);
    },

    handleChildrenViewClose () {
      this.$_setTitle();
    },
    handleDataUpdate () {
      this.fetchLogs();
    },

    buildLinkWithQuery (path) {
      return {
        path,
        query: this.$route.query,
      };
    },

    clearFilterTimeout () {
      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
        this.filterTimeout = null;
      }
    },
  },
  render () {
    if (!this.hasReadPermission) {
      return (
        <ErrorAccessDenied />
      );
    }

    const {
      $style,
      isFetching,
      buildLinkWithQuery,
      hasWorkerReadPermission,
      hasProjectReadPermission,
      hasTaskReadPermission,
      hasWritePermission,
    } = this;

    const canCreate = hasWritePermission
      && hasWorkerReadPermission
      && hasProjectReadPermission;

    return (
      <div class={$style.wrapper}>
        <u-title class={[$style.header, $style.section]}>
          <span>
            Лог действий
          </span>

          <span class={$style.actions}>
            <router-link
              class="u-btn u-btn_outline u-btn_primary u-btn_square"
              to={buildLinkWithQuery('/logs/export')}
            >
              <IconFileDownload />
            </router-link>
            {canCreate && (
              <router-link
                class="u-btn u-btn_outline u-btn_primary u-btn_square"
                to={buildLinkWithQuery('/logs/create')}
              >
                <IconPlus />
              </router-link>
            )}
          </span>
        </u-title>

        <div class={$style.section}>
          <LogsFilter
            value={this.filter}
            workerOptions={this.workerOptions}
            projectOptions={this.projectOptions}
            onChange={this.handleFilterChange}
          />
        </div>

        {isFetching ? (
          <u-spinner />
        ) : (
          <LogsList
            data={this.logs}
            class={[$style.section, $style.section_list]}
            linkBuilder={buildLinkWithQuery}
            canReadWorker={hasWorkerReadPermission}
            canReadProject={hasProjectReadPermission}
            canReadTask={hasTaskReadPermission}
            canEdit={hasWritePermission}
            dateIsWeekend={this.dateIsWeekend}
          />
        )}

        <router-view
          onClose={this.handleChildrenViewClose}
          onDataupdate={this.handleDataUpdate}
        />
      </div>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 5em);
}

.actions {
  margin-left: auto;

  & > * + * {
    margin-left: .5em;
  }
}

.section {
  flex-grow: 0;
  flex-shrink: 0;
  min-height: 0;

  &_list {
    flex-grow: 1;
    flex-shrink: 1;
    margin: 0 -16px;
    overflow: auto;
  }
}

.header {
  align-items: center;
  display: flex;

  &ExportLink {
    font-size: .5em;
    margin-left: 1em;
  }
}
</style>
